import { getUrlLocale } from "@/core/utils";
import { useRouter } from "next/router";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
} from "react";

interface HeaderContextType {
  // State
  isScrolled: boolean;
  canExpandDropdown?: boolean;
  headerVisible: boolean;
  isHeaderFixed: boolean;
  ignoreScroll: boolean;
  dropdownExpanded?: boolean;
  menuExpanded?: boolean;
  expandedMenu: HTMLDivElement | null;
  scrollDirection: "up" | "down" | null;
  currentLanguage?: string;
  headerRef: React.RefObject<HTMLDivElement>;
  // Actions
  setIsScrolled: (value: boolean) => void;
  setCanExpandDropdown: (value: boolean) => void;
  setHeaderVisible: (value: boolean) => void;
  setIsHeaderFixed: (value: boolean) => void;
  setIgnoreScroll: (value: boolean) => void;
  setDropdownExpanded: (value: boolean) => void;
  setMenuExpanded: (value: boolean) => void;
  setHeaderRef: (node: HTMLDivElement | null) => void;
  setExpandedMenu: (node: HTMLDivElement | null) => void;
  setScrollDirection: (value: "up" | "down" | null) => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

interface HeaderProviderProps {
  children: ReactNode;
}

export const HeaderProvider: React.FC<HeaderProviderProps> = ({ children }) => {
  const router = useRouter();
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollDirection, _setScrollDirection] = useState<"up" | "down" | null>(
    null
  );
  const [headerVisible, setHeaderVisible] = useState(true);
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [ignoreScroll, setIgnoreScroll] = useState(false);
  const [dropdownExpanded, setDropdownExpanded] = useState(false);
  const [canExpandDropdown, setCanExpandDropdown] = useState(true);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const [expandedMenu, setExpandedMenu] = useState<HTMLDivElement | null>(null);
  const [currentLanguage] = useState<string>(getUrlLocale(router.asPath));

  const setHeaderRef = (node: HTMLDivElement | null) => {
    if (node) {
      headerRef.current = node;
    } else {
      headerRef.current = null;
    }
  };


  const setScrollDirection = (value: "up" | "down" | null) => {
    _setScrollDirection(value);
    setHeaderVisible(value === "up");
  };

  const value = {
    isScrolled,
    setIsScrolled,
    scrollDirection,
    setScrollDirection,
    canExpandDropdown,
    setCanExpandDropdown,
    currentLanguage,
    menuExpanded,
    headerVisible,
    expandedMenu,
    setExpandedMenu,
    setHeaderVisible,
    setHeaderRef,
    setIsHeaderFixed,
    isHeaderFixed,
    headerRef,
    ignoreScroll,
    setIgnoreScroll,
    dropdownExpanded,
    setDropdownExpanded,
    setMenuExpanded,
  };

  return (
    <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>
  );
};

export const useHeader = () => {
  const context = useContext(HeaderContext);
  return context || {
    isScrolled: false,
    headerVisible: true,
    isHeaderFixed: false,
    ignoreScroll: false,
    scrollDirection: null,
    canExpandDropdown: false,
    currentLanguage: 'en',
    expandedMenu: null,
    headerRef: { current: null },
    setExpandedMenu: () => {},
    setCanExpandDropdown: () => {},
    setIsScrolled: () => {},
    setHeaderVisible: () => {},
    setIsHeaderFixed: () => {},
    setIgnoreScroll: () => {},
    setDropdownExpanded: () => {},
    setMenuExpanded: () => {},
    setHeaderRef: () => {},
    setScrollDirection: () => {},
  };
};
